import React, { useEffect, useRef, useState, useContext } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';

const SECOND_HAND_TRANSITION = 'transform 0.05s linear';
const SECOND_HAND_COLORS = {
  light: '#d9534f',
  dark: '#e74c3c'
};
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_HALF_DAY = 12 * SECONDS_PER_HOUR;
const MINUTE_DEGREES_PER_SECOND = 360 / SECONDS_PER_HOUR; // 0.1° per second
const HOUR_DEGREES_PER_SECOND = 360 / SECONDS_PER_HALF_DAY; // 1/120° per second


const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
const LARGE_CLOCK_THRESHOLD = 120;
const SUNRISE_SECONDS = 7 * SECONDS_PER_HOUR;
const DAY_DURATION_SECONDS = 12 * SECONDS_PER_HOUR; // 7am-7pm sweep across the upper arc
const SUNSET_SECONDS = SUNRISE_SECONDS + DAY_DURATION_SECONDS;
const MOONRISE_SECONDS = 19 * SECONDS_PER_HOUR; // 7pm
const NIGHT_DURATION_SECONDS = 12 * SECONDS_PER_HOUR; // 7pm-7am sweep across lower arc
const MOONSET_SECONDS = (MOONRISE_SECONDS + NIGHT_DURATION_SECONDS) % SECONDS_PER_DAY; // 7am
const ARC_START_DEGREES = 270; // 9 o'clock
const ARC_SWEEP_DEGREES = 180; // Upward path to 3 o'clock
const ORBIT_GAP_PX = 6;
const SUN_SIZE_MULTIPLIER = 1.125; // Reduced by 25% from previous 1.5x size

const AnalogClock = ({ size = 16, className = '', onClick }) => {
  const { effectiveTheme, THEME_DARK } = useContext(ThemeContext);
  const secondHandColor = effectiveTheme === THEME_DARK
    ? SECOND_HAND_COLORS.dark
    : SECOND_HAND_COLORS.light;
  const isLargeClock = size >= 100;
  const secondHandThickness = isLargeClock ? Math.max(2, size * 0.01) : 1;
  const secondHandLength = isLargeClock ? 44 : 30;
  const secondHandTailLength = isLargeClock ? Math.min(size * 0.12, 18) : 0;
  const secondHandShadow = isLargeClock ? '0 0 4px rgba(0, 0, 0, 0.25)' : 'none';
  const clockRef = useRef(null);
  const hourHandRef = useRef(null);
  const minuteHandRef = useRef(null);
  const secondHandRef = useRef(null);
  const cumulativeSecondAngleRef = useRef(0);
  const lastSecondsRef = useRef(null);
  const cumulativeMinuteAngleRef = useRef(0);
  const lastMinuteSecondsRef = useRef(null);
  const cumulativeHourAngleRef = useRef(0);
  const lastHourSecondsRef = useRef(null);
  // Use state to force re-renders and tracking
  const [currentTime, setCurrentTime] = useState(new Date());

  // Function to calculate rotation angles based on current time
  const updateClockHands = () => {
    // Get the current time directly from the system
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();

    // Track continuous hour rotation to avoid backwards spins at 12 o'clock
    const hourCycleSeconds = (hours % 12) * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
    if (lastHourSecondsRef.current === null) {
      lastHourSecondsRef.current = hourCycleSeconds;
      cumulativeHourAngleRef.current = ((hours % 12) * 30) + (minutes * 0.5) + (seconds * HOUR_DEGREES_PER_SECOND) - 90;
    } else if (hourCycleSeconds !== lastHourSecondsRef.current) {
      let hourSecondsDelta = hourCycleSeconds - lastHourSecondsRef.current;
      if (hourSecondsDelta < 0) {
        hourSecondsDelta += SECONDS_PER_HALF_DAY;
      }
      cumulativeHourAngleRef.current += hourSecondsDelta * HOUR_DEGREES_PER_SECOND;
      lastHourSecondsRef.current = hourCycleSeconds;
    }
    const hourAngle = cumulativeHourAngleRef.current;

    // Track minute rotation continuously (6° per minute, 0.1° per second)
    const minuteCycleSeconds = minutes * SECONDS_PER_MINUTE + seconds;
    if (lastMinuteSecondsRef.current === null) {
      lastMinuteSecondsRef.current = minuteCycleSeconds;
      cumulativeMinuteAngleRef.current = (minutes * 6) + (seconds * MINUTE_DEGREES_PER_SECOND) - 90;
    } else if (minuteCycleSeconds !== lastMinuteSecondsRef.current) {
      let minuteSecondsDelta = minuteCycleSeconds - lastMinuteSecondsRef.current;
      if (minuteSecondsDelta < 0) {
        minuteSecondsDelta += SECONDS_PER_HOUR;
      }
      cumulativeMinuteAngleRef.current += minuteSecondsDelta * MINUTE_DEGREES_PER_SECOND;
      lastMinuteSecondsRef.current = minuteCycleSeconds;
    }
    const minuteAngle = cumulativeMinuteAngleRef.current;

    // Second hand: keep angle monotonic so it never jumps backward
    if (lastSecondsRef.current === null) {
      lastSecondsRef.current = seconds;
      cumulativeSecondAngleRef.current = seconds * 6 - 90;
    } else if (seconds !== lastSecondsRef.current) {
      let secondsDelta = seconds - lastSecondsRef.current;
      if (secondsDelta < 0) {
        secondsDelta += 60;
      }
      cumulativeSecondAngleRef.current += secondsDelta * 6;
      lastSecondsRef.current = seconds;
    }

    const secondAngle = cumulativeSecondAngleRef.current;

    // Apply rotations with consistent transforms
    if (hourHandRef.current) {
      hourHandRef.current.style.transform = `rotate(${hourAngle}deg)`;
    }

    if (minuteHandRef.current) {
      minuteHandRef.current.style.transform = `rotate(${minuteAngle}deg)`;
    }

    if (secondHandRef.current) {
      secondHandRef.current.style.transform = `rotate(${secondAngle}deg)`;
    }

    // Update current time in state
    setCurrentTime(now);
  };
  
  useEffect(() => {
    // Set initial positions immediately when component mounts
    updateClockHands();

    // Update every second for the second hand
    const interval = setInterval(() => {
      updateClockHands();
    }, 1000); // Every second

    return () => clearInterval(interval);
  }, []);

  // Generate hour markers based on size
  const renderHourMarkers = () => {
    if (size < 100) return null;

    return Array.from({ length: 12 }).map((_, i) => {
      const angle = i * 30;
      const radian = (angle - 90) * (Math.PI / 180); // Start from 12 o'clock

      // Position numbers slightly inward from the edge
      const numberDistance = 38; // Numbers are a bit closer to center
      const numX = 50 + numberDistance * Math.cos(radian);
      const numY = 50 + numberDistance * Math.sin(radian);

      // Display hour number (1-12)
      const hourNumber = i === 0 ? 12 : i;

      return (
        <div
          key={i}
          style={{
            position: 'absolute',
            fontSize: `${size / 15}px`,
            fontWeight: i === 0 ? 'bold' : '500',
            top: `${numY}%`,
            left: `${numX}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: 0
          }}
        >
          {hourNumber}
        </div>
      );
    });
  };

  // Generate minute markers on outer circle
  const renderMinuteMarkers = () => {
    if (size < 100) return null;

    return Array.from({ length: 60 }).map((_, i) => {
      const angle = i * 6; // Each minute is 6 degrees
      const radian = (angle - 90) * (Math.PI / 180); // Start from 12 o'clock

      // Position on outer circle
      const outerDistance = 46;

      const outerX = 50 + outerDistance * Math.cos(radian);
      const outerY = 50 + outerDistance * Math.sin(radian);

      // Differentiate 5-minute marks from 1-minute marks
      const isFiveMinuteMark = i % 5 === 0;

      if (isFiveMinuteMark) {
        // 5-minute marks as dots
        return (
          <div
            key={`min-${i}`}
            style={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              backgroundColor: 'currentColor',
              borderRadius: '50%',
              top: `${outerY}%`,
              left: `${outerX}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 0,
              opacity: 0.7
            }}
          />
        );
      } else {
        // 1-minute marks as small dashes
        return (
          <div
            key={`min-${i}`}
            style={{
              position: 'absolute',
              width: '1px',
              height: '3px',
              backgroundColor: 'currentColor',
              top: `${outerY}%`,
              left: `${outerX}%`,
              transform: `translate(-50%, -50%) rotate(${angle}deg)`,
              transformOrigin: 'center',
              zIndex: 0,
              opacity: 0.7
            }}
          />
        );
      }
    });
  };

  const renderCelestialTrack = () => {
    if (size < LARGE_CLOCK_THRESHOLD) return null;

    const secondsSinceMidnight = currentTime.getHours() * SECONDS_PER_HOUR
      + currentTime.getMinutes() * SECONDS_PER_MINUTE
      + currentTime.getSeconds();

    const clampProgress = (value) => Math.min(Math.max(value, 0), 1);

    const sunProgress = clampProgress((secondsSinceMidnight - SUNRISE_SECONDS) / DAY_DURATION_SECONDS);

    let moonProgress = 0;
    let withinMoonWindow = false;
    if (secondsSinceMidnight >= MOONRISE_SECONDS || secondsSinceMidnight < MOONSET_SECONDS) {
      withinMoonWindow = true;
      const moonElapsedSeconds = secondsSinceMidnight >= MOONRISE_SECONDS
        ? secondsSinceMidnight - MOONRISE_SECONDS
        : secondsSinceMidnight + (SECONDS_PER_DAY - MOONRISE_SECONDS);
      moonProgress = clampProgress(moonElapsedSeconds / NIGHT_DURATION_SECONDS);
    }

    const withinSunWindow = secondsSinceMidnight >= SUNRISE_SECONDS && secondsSinceMidnight < SUNSET_SECONDS;

    let topBody;
    let topProgress;

    if (withinSunWindow) {
      topBody = 'sun';
      topProgress = sunProgress;
    } else if (withinMoonWindow) {
      topBody = 'moon';
      topProgress = moonProgress;
    } else if (secondsSinceMidnight < SUNRISE_SECONDS) {
      topBody = 'sun';
      topProgress = 0;
    } else if (secondsSinceMidnight < MOONRISE_SECONDS) {
      topBody = 'moon';
      topProgress = 0;
    } else {
      topBody = 'sun';
      topProgress = 0;
    }

    const bottomBody = topBody === 'sun' ? 'moon' : 'sun';

    const baseBodySize = Math.max(12, size * 0.12);
    const sunBodySize = baseBodySize * SUN_SIZE_MULTIPLIER;
    const moonBodySize = baseBodySize;
    const getOrbitRadius = (bodySize) => (size / 2) + (bodySize / 2) + ORBIT_GAP_PX; // Maintain consistent gap for each body
    const center = size / 2;

    const angle = (ARC_START_DEGREES + topProgress * ARC_SWEEP_DEGREES + 360) % 360;
    const topRadian = (angle - 90) * (Math.PI / 180);
    const bottomAngle = (angle + 180) % 360;
    const bottomRadian = (bottomAngle - 90) * (Math.PI / 180);

    const toPosition = (radian, bodySize) => {
      const radius = getOrbitRadius(bodySize);
      return {
        left: center + radius * Math.cos(radian),
        top: center + radius * Math.sin(radian)
      };
    };

    const topPosition = toPosition(topRadian, topBody === 'sun' ? sunBodySize : moonBodySize);
    const bottomPosition = toPosition(bottomRadian, bottomBody === 'sun' ? sunBodySize : moonBodySize);

    const renderBody = (type, position, placement, sizePx) => (
      <div
        key={`${type}-${placement}`}
        className={`celestial-body ${type}`}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${sizePx}px`,
          height: `${sizePx}px`,
          transform: 'translate(-50%, -50%)'
        }}
      />
    );

    return (
      <div className="celestial-track">
        {renderBody(topBody, topPosition, 'top', topBody === 'sun' ? sunBodySize : moonBodySize)}
        {renderBody(bottomBody, bottomPosition, 'bottom', bottomBody === 'sun' ? sunBodySize : moonBodySize)}
      </div>
    );
  };

  return (
    <div 
      ref={clockRef}
      className={`analog-clock ${className}`} 
      style={{ 
        position: 'relative',
        width: `${size}px`, 
        height: `${size}px`,
        borderRadius: '50%',
        border: '1px solid currentColor',
        display: 'inline-block',
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: 'var(--card-bg, white)',
        boxShadow: size >= 100 ? 'inset 0 0 10px rgba(0, 0, 0, 0.1)' : 'none'
      }}
      onClick={onClick}
      title={currentTime.toLocaleTimeString()}
    >
      {/* Clock face */}
      <div className="clock-face" style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Inner colored circle */}
        {size >= 100 && (
          <div
            style={{
              position: 'absolute',
              width: '86%',
              height: '86%',
              borderRadius: '50%',
              backgroundColor: effectiveTheme === THEME_DARK
                ? 'rgba(13, 110, 253, 0.15)'
                : 'rgba(13, 110, 253, 0.1)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 0
            }}
          />
        )}

        {/* Day/night orbit */}
        {renderCelestialTrack()}

        {/* 5-minute markers */}
        {renderMinuteMarkers()}

        {/* Hour markers */}
        {renderHourMarkers()}

        {/* Hour hand */}
        <div
          ref={hourHandRef}
          className="hour-hand"
          style={{
            position: 'absolute',
            width: '34%',
            height: size >= 100 ? '3px' : '2px',
            backgroundColor: 'currentColor',
            top: '50%',
            left: '50%',
            transformOrigin: '0 50%',
            transform: 'rotate(0deg)',
            zIndex: 1
          }}
        />
        
        {/* Minute hand */}
        <div
          ref={minuteHandRef}
          className="minute-hand"
          style={{
            position: 'absolute',
            width: '42%',
            height: size >= 100 ? '2px' : '1px',
            backgroundColor: 'currentColor',
            top: '50%',
            left: '50%',
            transformOrigin: '0 50%',
            transform: 'rotate(0deg)',
            zIndex: 2
          }}
        />

        {/* Second hand */}
        <div
          ref={secondHandRef}
          className="second-hand"
          style={{
            position: 'absolute',
            width: `${secondHandLength}%`,
            height: `${secondHandThickness}px`,
            backgroundColor: secondHandColor,
            top: '50%',
            left: '50%',
            transformOrigin: '0 50%',
            transform: 'rotate(0deg)',
            transition: SECOND_HAND_TRANSITION,
            zIndex: 3,
            boxShadow: secondHandShadow
          }}
        >
          {secondHandTailLength > 0 && (
            <div
              style={{
                position: 'absolute',
                width: `${secondHandTailLength}px`,
                height: '100%',
                backgroundColor: secondHandColor,
                left: `-${secondHandTailLength}px`,
                top: 0,
                opacity: 0.9
              }}
            />
          )}
        </div>

        {/* Center dot */}
        <div
          className="center-dot"
          style={{
            position: 'absolute',
            width: size >= 100 ? '6px' : '3px',
            height: size >= 100 ? '6px' : '3px',
            backgroundColor: 'currentColor',
            borderRadius: '50%',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 4
          }}
        />
      </div>
    </div>
  );
};

export default AnalogClock;
